// What is worth reading of an email inside a conversation on the dashboard: what this email says, without the earlier emails
// quoted under a reply or the signature at the end. Plain JavaScript so node can test it without a browser.

/** "Thanks," "Kind regards" and the like: a line that starts the signature when it comes near the end. */
const SIGN_OFF = /^(thanks|thank you|many thanks|thanks again|thanks so much|kind regards|best regards|warm regards|regards|best wishes|best|all the best|cheers|sincerely|yours sincerely|yours faithfully)[,.!]?$/i;

/** How far from the end a sign-off may be and still be taken as the start of the signature. */
const SIGN_OFF_REACH = 15;

/** How far from the end contact details may start and still be taken as the signature. */
const CONTACT_REACH = 25;

/** The legal small print and phone-sent lines organisations add under every email. */
const FOOTER = /^(view our (email )?disclaimer|disclaimer\b|the information (contained )?in this (e-?mail|message)|this (e-?mail|message)\b.*\b(confidential|intended|attachments?)|confidential(ity)?( notice)?:?$|registered (office|in england|charity|company)|(company|charity) (no|number|registration)\b|please consider the environment|sent from my (iphone|ipad|android|samsung|phone))/i;

/** A line of contact details: a labelled phone, email, web or social line, a bare web address or a bare phone number. */
const CONTACT = /^((tel|telephone|t|phone|ph|freephone|mob|mobile|m|fax|f|e|email|e-mail|web|w|website|a|address|x|twitter|facebook|linked\s?in|instagram)\s*:|www\.|<?https?:\/\/|\+?\d[\d\s()-]{8,}$)/i;

/** Lines of a signature above the contact details (a name, a job title): short, and not a sentence. */
const SIGNATURE_LINE = (line) => line !== '' && line.length <= 70 && !/[.?!:]$/.test(line);

/** A line as it reads, without the asterisks plain-text email uses for bold. */
const plain = (line) => line.replace(/\*/g, '').trim();

/** A picture's stand-in in plain text: "[image: Our logo]", "[Company Logo]". */
const PICTURE = /^\[(image|cid|company logo|logo)\b[^\]]*\]$/i;

/** A line that starts the earlier email quoted under a reply, so nothing from it on is new. */
function startsQuote(line, next) {
  return /^On .+wrote:$/i.test(line)
    || (/^On .+/i.test(line) && /wrote:$/i.test(next))
    || /^-{2,}\s*Original Message\s*-{2,}$/i.test(line)
    || /^_{8,}$/.test(line)
    || (/^From: .+/i.test(line) && /^(Sent|Date): /i.test(next));
}

/**
 * The text of an email without what came before it in the thread (quoted lines, "On … wrote:" and what follows, Outlook's
 * "From: / Sent:" block) or the signature after it: the legal small print, a sign-off such as "Thanks," near the end and the
 * lines under it, or the contact details at the end with the name and job title just above them. Pictures' stand-ins go too.
 * When that leaves nothing, the whole text is kept.
 */
export function readableText(text) {
  const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
  const kept = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = plain(lines[index]);
    if (/^-- ?$/.test(lines[index]) || startsQuote(line, plain(lines[index + 1] ?? ''))) break;
    if (line.startsWith('>') || PICTURE.test(line)) continue;
    kept.push(lines[index].trimEnd());
  }
  // The small print, and everything under it.
  const footer = kept.findIndex((line) => FOOTER.test(plain(line)));
  if (footer > 0) kept.length = footer;
  cutSignOff(kept);
  // The contact details near the end, and the name and job title just above them (short lines, possibly after a blank line).
  // A phone number in the middle of a longer message is part of the message.
  const contact = kept.findIndex((line, index) => index > 0 && index >= kept.length - CONTACT_REACH && CONTACT.test(plain(line)));
  if (contact > 0) {
    let start = contact;
    let above = contact - 1;
    while (above >= 0 && plain(kept[above]) === '') above -= 1;
    let block = 0;
    while (above >= 0 && block < 5 && SIGNATURE_LINE(plain(kept[above]))) { above -= 1; block += 1; }
    // When the signature runs to the top, it is all the email says (an automatic reply, say), so it stays.
    if (above >= 0) {
      if (plain(kept[above]) === '') start = above + 1;
      kept.length = start;
    }
  }
  // With a long signature gone, a sign-off that was too far from the end to see may now be at it.
  cutSignOff(kept);
  const readable = kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  return readable || String(text ?? '').trim();
}

/** Cuts the lines at a sign-off near the end, and everything under it. */
function cutSignOff(kept) {
  for (let index = kept.length - 1; index >= Math.max(0, kept.length - SIGN_OFF_REACH); index -= 1) {
    if (SIGN_OFF.test(plain(kept[index]))) {
      kept.length = index;
      return;
    }
  }
}

/** A subject without its "Re:" and "Fwd:", to tell whether a reply changed it. */
export const baseSubject = (subject) => String(subject ?? '').replace(/^\s*((re|fwd?|fw)\s*:\s*)+/i, '').trim().toLowerCase();
